import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import PapaParse from 'papaparse'
import { v4 as uuidv4 } from 'uuid'

import { Col, View } from '@amzn/stencil-react-components/layout'
import { MessageBanner, MessageBannerType } from '@amzn/stencil-react-components/message-banner'

import { ItemContainerMetadataDTO } from 'src/models/dto/item-pools/ItemPoolMetadataDTO'
import { TemplateValidationErrorsThrowable } from 'src/models/dto/TemplateValidationError'
import { CreateStep } from 'src/pages/import-content/CreateStep'
import { ImportContentHeader } from 'src/pages/import-content/ImportContentHeader'
import {
    countTemplateItems,
    groupRowsByContainer,
    ImportStep,
    mapContainerErrorsToRows,
    ParsedSpreadsheet,
    RowValidation,
    validateContainers,
    validateSpreadsheet,
} from 'src/pages/import-content/ImportContentModel'
import { ReviewStep } from 'src/pages/import-content/ReviewStep'
import { UploadStep } from 'src/pages/import-content/UploadStep'
import { ModuleService } from 'src/services/backend/ModuleService'
import {
    CONTAINER_COLUMN,
    downloadSampleSpreadsheet,
    getSpreadsheetColumns,
} from 'src/services/import-content/TemplateSpreadsheetService'
import { ItemPoolService } from 'src/services/item-pools/ItemPoolService'
import { uploadImportSpreadsheetToS3 } from 'src/services/media/S3FileHandler'

const S3_IMPORT_PREFIX = 'item-pool-imports'

const ITEMS_CONTAINER_INFIX = '_ItemsContainer_'

const MODULE_VERSION_ID_PARAM_KEY = 'moduleVersionId'

export const ImportContentPage = () => {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const templateModuleVersionId = (searchParams.get(MODULE_VERSION_ID_PARAM_KEY) ?? '').trim()

    const [step, setStep] = useState(ImportStep.Upload)

    const [moduleName, setModuleName] = useState<string | undefined>(undefined)
    const [savedAt, setSavedAt] = useState<Date | undefined>(undefined)
    const [placeholders, setPlaceholders] = useState<string[]>([])
    const [itemsPerRow, setItemsPerRow] = useState(0)
    const [isLoadingModule, setIsLoadingModule] = useState(false)
    const [loadError, setLoadError] = useState<string | undefined>(undefined)

    const [file, setFile] = useState<File | undefined>(undefined)
    const [spreadsheet, setSpreadsheet] = useState<ParsedSpreadsheet | undefined>(undefined)
    const [reviewPage, setReviewPage] = useState(0)
    const [rowValidation, setRowValidation] = useState<RowValidation | undefined>(undefined)
    const [isValidatingRows, setIsValidatingRows] = useState(false)
    const [validatedRowCount, setValidatedRowCount] = useState(0)

    const [isPreviewing, setIsPreviewing] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
    const [containers, setContainers] = useState<ItemContainerMetadataDTO[] | undefined>(undefined)

    const plannedContainers = useMemo(
        () =>
            spreadsheet
                ? groupRowsByContainer(spreadsheet.rows).map((group) => ({
                      name: `${moduleName ?? ''}${ITEMS_CONTAINER_INFIX}${group.name}`,
                      rowCount: group.rowIndices.length,
                  }))
                : [],
        [spreadsheet, moduleName]
    )

    const validation = useMemo(
        () => (spreadsheet ? validateSpreadsheet(spreadsheet, placeholders) : undefined),
        [spreadsheet, placeholders]
    )

    const loadTemplate = useCallback(async (versionId: string) => {
        setLoadError(undefined)
        setModuleName(undefined)
        setSavedAt(undefined)
        setPlaceholders([])
        setIsLoadingModule(true)

        try {
            const moduleVersion = await ModuleService.loadModuleVersionDTO(versionId)
            setModuleName(moduleVersion.content.name)
            setSavedAt(moduleVersion.updatedAt)
            setPlaceholders(getSpreadsheetColumns(moduleVersion.content))
            setItemsPerRow(countTemplateItems(moduleVersion.content))
        } catch (e) {
            console.error('Failed to load template module', e)
            setLoadError(e instanceof Error ? e.message : 'Failed to load the template module.')
        } finally {
            setIsLoadingModule(false)
        }
    }, [])

    useEffect(() => {
        if (templateModuleVersionId) {
            void loadTemplate(templateModuleVersionId)
        } else {
            setLoadError('No template module was provided. Start the import from the module.')
        }
    }, [templateModuleVersionId, loadTemplate])

    const dryRanFor = useRef<ParsedSpreadsheet | undefined>(undefined)

    const onFileAttached = useCallback((attached?: File) => {
        setFile(attached)
        setSpreadsheet(undefined)
        setReviewPage(0)
        setRowValidation(undefined)
        setErrorMessage(undefined)
        dryRanFor.current = undefined
        setIsValidatingRows(false)
        setValidatedRowCount(0)
        if (!attached) {
            return
        }
        PapaParse.parse<Record<string, string>>(attached, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                const headers = results.meta.fields ?? []
                const rows = results.data
                const containerCount = headers.includes(CONTAINER_COLUMN)
                    ? new Set(rows.map((row) => (row[CONTAINER_COLUMN] ?? '').trim())).size
                    : 1
                setSpreadsheet({ headers, rowCount: rows.length, containerCount, rows })
            },
        })
    }, [])

    const onDownloadSample = useCallback(() => {
        downloadSampleSpreadsheet(placeholders, moduleName)
    }, [placeholders, moduleName])

    const isSpreadsheetUsable =
        !!moduleName && !!spreadsheet && !!validation && validation.missing.length === 0

    const hasFlaggedRows = Object.keys(rowValidation?.byRow ?? {}).length > 0
    const hasGeneralErrors = (rowValidation?.general ?? []).length > 0

    const canSubmit =
        isSpreadsheetUsable &&
        !!file &&
        !isSubmitting &&
        !isValidatingRows &&
        !hasFlaggedRows &&
        !hasGeneralErrors
    const canPreview = isSpreadsheetUsable && !isPreviewing && !isSubmitting

    const advancedFor = useRef<ParsedSpreadsheet | undefined>(undefined)
    useEffect(() => {
        if (isSpreadsheetUsable && advancedFor.current !== spreadsheet) {
            advancedFor.current = spreadsheet
            setStep(ImportStep.Review)
        }
    }, [isSpreadsheetUsable, spreadsheet])

    useEffect(() => {
        if (step !== ImportStep.Review || !spreadsheet || dryRanFor.current === spreadsheet) {
            return
        }
        dryRanFor.current = spreadsheet

        const isCurrent = () => dryRanFor.current === spreadsheet
        setRowValidation(undefined)
        setIsValidatingRows(true)
        setValidatedRowCount(0)

        void validateContainers(
            groupRowsByContainer(spreadsheet.rows).map(({ rowIndices }) => rowIndices),
            async (rowIndices) => {
                const errors = await ItemPoolService.validateImportedContainer(
                    templateModuleVersionId,
                    rowIndices.map((rowIndex) => spreadsheet.rows[rowIndex])
                )
                return mapContainerErrorsToRows(errors, rowIndices)
            },
            (completed) => {
                if (isCurrent()) {
                    setValidatedRowCount(completed)
                }
            }
        ).then((validated) => {
            if (isCurrent()) {
                setRowValidation(validated)
                setIsValidatingRows(false)
                if (validated.general.length > 0) {
                    setErrorMessage(validated.general.join(' '))
                }
            }
        })
    }, [step, spreadsheet, templateModuleVersionId])

    const onPreviewRow = useCallback(
        async (row: Record<string, string>) => {
            setErrorMessage(undefined)
            setIsPreviewing(true)
            try {
                const moduleDtoJson = await ItemPoolService.buildImportedModulePreview(
                    templateModuleVersionId,
                    [row]
                )
                const versionId = (JSON.parse(moduleDtoJson) as { version: string }).version
                const previewUrl = await ItemPoolService.previewModuleTemplate(
                    versionId,
                    moduleDtoJson
                )
                window.open(previewUrl, '_blank', 'noopener,noreferrer')
            } catch (e) {
                console.error('Preview failed', e)
                setErrorMessage(e instanceof Error ? e.message : 'Failed to generate the preview.')
            } finally {
                setIsPreviewing(false)
            }
        },
        [templateModuleVersionId]
    )

    const onSubmit = useCallback(async () => {
        /* istanbul ignore if: Create is aria-disabled until a usable spreadsheet is attached */
        if (!file) {
            return
        }
        setErrorMessage(undefined)
        setContainers(undefined)
        setRowValidation(undefined)
        setIsSubmitting(true)
        setStep(ImportStep.Create)
        try {
            const spreadsheetS3Path = `${S3_IMPORT_PREFIX}/${uuidv4()}-${file.name}`
            await uploadImportSpreadsheetToS3(file, spreadsheetS3Path)

            const { versionId } = await ItemPoolService.importedItemPool({
                templateModuleVersionId,
                spreadsheetS3Path,
            })

            const generated = await ItemPoolService.getItemPoolItems(versionId)
            setContainers(generated ?? [])
        } catch (e) {
            console.error('Import content failed', e)
            if (e instanceof TemplateValidationErrorsThrowable) {
                setStep(ImportStep.Review)
                setErrorMessage(
                    [
                        'The spreadsheet did not pass validation. Nothing was imported.',
                        ...e.errors.map(({ message }) => message),
                    ].join(' ')
                )
            } else {
                setErrorMessage(
                    e instanceof Error ? e.message : 'Failed to import content. Please try again.'
                )
            }
        } finally {
            setIsSubmitting(false)
        }
    }, [file, templateModuleVersionId])

    const onStepSelected = useCallback(
        (selected: ImportStep) => {
            if (isSubmitting || containers !== undefined) {
                return
            }
            if (selected < step || (selected === ImportStep.Review && isSpreadsheetUsable)) {
                setStep(selected)
            }
        },
        [step, isSubmitting, containers, isSpreadsheetUsable]
    )

    return (
        <View padding='S400'>
            <Col gridGap='S400' width='100%'>
                <ImportContentHeader
                    moduleName={moduleName}
                    moduleVersionId={templateModuleVersionId}
                    savedAt={savedAt}
                    currentStep={step}
                    isReviewAvailable={isSpreadsheetUsable && containers === undefined}
                    onStepSelected={onStepSelected}
                />

                {errorMessage && (
                    <MessageBanner type={MessageBannerType.Error} dataTestId='import-content-error'>
                        {errorMessage}
                    </MessageBanner>
                )}

                {step === ImportStep.Upload && (
                    <UploadStep
                        isLoadingModule={isLoadingModule}
                        loadError={loadError}
                        moduleName={moduleName}
                        file={file}
                        placeholders={placeholders}
                        spreadsheet={spreadsheet}
                        validation={validation}
                        onFileAttached={onFileAttached}
                        onDownloadSample={onDownloadSample}
                    />
                )}

                {step === ImportStep.Review && spreadsheet && (
                    <ReviewStep
                        spreadsheet={spreadsheet}
                        page={reviewPage}
                        onPageChange={setReviewPage}
                        canPreview={canPreview}
                        onPreviewRow={onPreviewRow}
                        isPreviewing={isPreviewing}
                        canSubmit={canSubmit}
                        onSubmit={onSubmit}
                        onBack={() => setStep(ImportStep.Upload)}
                        rowValidation={rowValidation}
                        isValidatingRows={isValidatingRows}
                        validatedRowCount={validatedRowCount}
                        itemsPerRow={itemsPerRow}
                    />
                )}

                {step === ImportStep.Create && (
                    <CreateStep
                        fileName={file?.name}
                        rowCount={spreadsheet?.rowCount ?? 0}
                        plannedContainers={plannedContainers}
                        isSubmitting={isSubmitting}
                        containers={containers}
                        hasError={!!errorMessage && !isSubmitting}
                        onBack={() => setStep(ImportStep.Review)}
                        onDone={() =>
                            navigate(
                                `/module-builder?${MODULE_VERSION_ID_PARAM_KEY}=${templateModuleVersionId}`
                            )
                        }
                    />
                )}
            </Col>
        </View>
    )
}
